import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, PhoneCall } from 'lucide-react';
import { metaInitiateCheckout } from '../../lib/metaPixel';

// Static mirror of backend/routes/billing.py PLANS (Jul 2026 four-plan lineup).
const TIERS = [
  {
    id: 'starter', name: 'Starter', price: '19.99',
    tagline: 'For do-it-yourselfers who want to stay organized',
    features: [
      'Single user (owner only)',
      '1 bank account synced via Plaid',
      '200 AI credits / month',
      '2 GB document storage',
      'AI auto-categorization + OCR receipts',
      'Invoicing & basic reports',
    ],
  },
  {
    id: 'pro', name: 'Pro', price: '49.99', ribbon: 'Most popular', popular: true,
    tagline: 'More power + more AI for growing businesses',
    features: [
      'Everything in Starter',
      '5 users · multi-workspace, members & roles',
      '4 bank accounts · Plaid auto-sync',
      '1,500 AI credits / month',
      '25 GB document storage',
      'Daily Brief · e-signatures · time tracking',
      'Accept payments on invoices (Stripe)',
      'Client portal',
    ],
  },
  {
    id: 'platinum', name: 'Platinum', price: '99.99', ribbon: 'Advisor call included',
    tagline: 'Expert guidance — a business advisor in your corner',
    features: [
      'Everything in Pro',
      '15-min monthly strategy call with a business advisor',
      'Unlimited users',
      '10 bank accounts',
      '4,000 AI credits / month',
      '100 GB document storage',
      'Gmail + Calendar integration',
      'Advanced reports + exports',
    ],
  },
  {
    id: 'done_for_you', name: 'Done For You', price: '299.99', ribbon: 'Tax return included',
    tagline: 'We handle it — bookkeeping, tax return & advisor calls',
    features: [
      'Everything in Platinum',
      'Annual tax return prep & filing — INCLUDED',
      'Dedicated CPA / EA — books done for you monthly',
      '15-min monthly strategy call with a business advisor',
      '150 GB storage · 15 banks · 5,000 credits',
      'White-glove migration from QuickBooks',
      'Priority support',
    ],
  },
];

export function HubPricing({ fade }) {
  return (
    <section id="pricing" className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="pricing-section">
      <motion.div {...(fade ? fade(0) : {})} className="text-center">
        <span className="inline-block rounded-full border border-[#27B84A]/40 bg-[#F2FBF4] px-4 py-1.5 text-xs font-bold tracking-widest text-[#118A35] dark:bg-[#0E1A11] dark:text-[#27B84A]">
          PRICING
        </span>
        <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
          Simple plans. Every feature listed. <span className="text-[#27B84A]">No surprises.</span>
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[#596273] dark:text-[#AEB6B1]">
          Start with a 7-day free trial on any plan. Upgrade, downgrade or cancel anytime.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        {TIERS.map((tier, i) => (
          <motion.div
            key={tier.id}
            {...(fade ? fade(0.05 * i) : {})}
            data-testid={`pricing-card-${tier.id}`}
            className={`relative flex flex-col rounded-3xl border p-6 ${
              tier.popular
                ? 'border-[#27B84A] bg-[#F2FBF4] shadow-xl shadow-green-600/10 dark:bg-[#0E1A11]'
                : 'border-[#E4E8E6] bg-white dark:border-[#1C2420] dark:bg-[#10150F]'
            }`}
          >
            {tier.ribbon && (
              <span className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-bold text-white shadow-sm ${
                tier.popular ? 'bg-[#27B84A]' : 'bg-[#FF7816]'
              }`}>
                {tier.ribbon}
              </span>
            )}
            <h3 className="text-lg font-extrabold">{tier.name}</h3>
            <p className="mt-1 text-xs text-[#596273] dark:text-[#8B948F]">{tier.tagline}</p>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-extrabold" data-testid={`pricing-price-${tier.id}`}>${tier.price}</span>
              <span className="text-sm text-[#596273] dark:text-[#8B948F]">/month</span>
            </div>
            <ul className="mt-5 flex-1 space-y-2.5">
              {tier.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-[#18202D] dark:text-[#D9DED9]">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[#27B84A]">
                    <Check className="h-2.5 w-2.5 text-white" strokeWidth={4} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to={`/login?mode=register&plan=${tier.id}&cadence=monthly`}
              data-testid={`pricing-cta-${tier.id}`}
              onClick={() => metaInitiateCheckout({ planId: tier.id, value: tier.price, planName: tier.name })}
              className={`mt-6 inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-bold transition-all hover:-translate-y-0.5 ${
                tier.popular
                  ? 'bg-[#27B84A] text-white shadow-lg shadow-green-600/25 hover:bg-[#118A35]'
                  : 'border border-[#E4E8E6] text-[#18202D] hover:border-[#27B84A] dark:border-[#1C2420] dark:text-white'
              }`}
            >
              Start free trial
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Paid CPA Advisor call strip */}
      <div className="mt-10 flex flex-col items-center justify-between gap-4 rounded-3xl border border-[#E4E8E6] bg-[#F7F9F8] p-6 sm:flex-row dark:border-[#1C2420] dark:bg-[#0D120E]" data-testid="pricing-book-call-strip">
        <div className="flex items-center gap-4">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#27B84A]/15">
            <PhoneCall className="h-5 w-5 text-[#27B84A]" />
          </span>
          <div>
            <p className="font-bold">Need one-on-one help? Book a call with our internal CPA Advisor.</p>
            <p className="text-sm text-[#596273] dark:text-[#8B948F]">$150/hr in 15-minute increments · Mon–Fri, 9am–4pm ET · pre-paid & instantly confirmed</p>
          </div>
        </div>
        <Link
          to="/book"
          data-testid="pricing-book-call-cta"
          className="whitespace-nowrap rounded-2xl bg-[#18202D] px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 dark:bg-white dark:text-[#18202D]"
        >
          Book a Call
        </Link>
      </div>
    </section>
  );
}
